import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MainService } from "core/main.service";
import { ViewService } from "core/view.service";
import { Sequence } from "process/element/sequence.interface";
import { BehaviorSubject, combineLatest, Observable, of, Subject } from "rxjs";
import { map, takeUntil, tap } from "rxjs/operators";
import { CardComponent } from "common/card/card.component";
import { ActiveFloatingActionButtonType } from "core/volatile-application-state.model";
import Prompt = stages.common.Prompt;

type ProcessView = stages.process.ProcessView;

@Component({
	selector: "stages-add-sequence",
	styleUrls: ["add-sequence.component.scss"],
	templateUrl: "add-sequence.component.html",
})
export class AddSequenceComponent implements OnInit, OnDestroy {
	protected menuItems: MenuItem[] = [];
	private sequences$ = new BehaviorSubject<Sequence[]>([]);

	private destroy$ = new Subject<boolean>();
	private menuItems$ = new BehaviorSubject<MenuItem[]>([]);
	selfElement$: Observable<ProcessView> | undefined;

	private isFloatingActionButtonAllowed$ = of(false);
	protected showFloatingActionButton = false;

	protected card: CardComponent;

	@Input() set sequences(sequences: Sequence[]) {
		this.sequences$.next(sequences);
	}

	constructor(
		private route: ActivatedRoute,
		private viewService: ViewService,
		private mainService: MainService,
		private cardComponent: CardComponent,
		private changeDetector: ChangeDetectorRef,
	) {
		this.card = this.cardComponent;
	}

	ngOnInit(): void {
		this.isFloatingActionButtonAllowed$ = this.mainService.applicationState.activeFloatingActionButtonType.pipe(
			map((activeButton) => {
				const isAllowed = this.mainService.isFloatingActionButtonActivationAllowed(
					activeButton,
					ActiveFloatingActionButtonType.SEQUENCES,
				);
				if (isAllowed) {
					this.mainService.applicationState.activeFloatingActionButtonType.next(
						ActiveFloatingActionButtonType.SEQUENCES,
					);
				}
				return isAllowed;
			}),
			takeUntil(this.destroy$),
		);

		combineLatest([
			this.mainService.applicationContext$,
			this.viewService.awaitSelfElementObservable(),
			this.sequences$,
		])
			.pipe(
				map(([applicationContext, selfElement, sequences]) => {
					const hasGenerateOption = sequences.find((option) => option.generate === true)?.generate;
					const createChildElementPrompts: Prompt[] =
						selfElement.promptContainer?.prompts.filter((p) => p.promptType === "NAMES") ?? [];
					const isProcessCreatorButtonVisible =
						!!hasGenerateOption && applicationContext.textCreatorConfigured && createChildElementPrompts.length > 0;
					this.updateMenuItems(selfElement, sequences, isProcessCreatorButtonVisible);
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();

		combineLatest([this.sequences$, this.menuItems$, this.isFloatingActionButtonAllowed$])
			.pipe(
				map(([sequences, menuItems, isFloatingActionButtonAllowed]) => {
					this.showFloatingActionButton = sequences.length > 0 && menuItems.length > 0 && isFloatingActionButtonAllowed;
					return this.showFloatingActionButton;
				}),
				tap(() => {
					this.changeDetector.detectChanges();
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();
	}

	updateMenuItems(selfElement: ProcessView, sequences: Sequence[], isProcessCreatorButtonVisible: boolean): void {
		this.menuItems = [];
		sequences.forEach((option: Sequence) => {
			const parent = option.level === "same" ? selfElement.parent : selfElement;

			const createChildElementIsAllowed: boolean = parent?.allowedOperations.CREATE_CHILD ?? false;
			const createAssociationIsAllowed: boolean = hasCreateAssociationPermission(selfElement, option);
			const menuItemIsAIGenerator: boolean = option.generate === true;

			if (!createChildElementIsAllowed || !createAssociationIsAllowed) {
				return;
			}

			if (menuItemIsAIGenerator) {
				if (isProcessCreatorButtonVisible) {
					this.menuItems.push(this.createMenuItem(option, "processcreator", ["ico ico-et-aitemplate"], "ai"));
				}
			} else {
				this.menuItems.push(
					this.createMenuItem(option, "sequence", this.getIconClasses(option.targetType, option.targetSubtype), ""),
				);
			}
		});

		this.menuItems$.next(this.menuItems);
	}

	createMenuItem(option: Sequence, path: string, iconCSS: string[], buttonCSS: string): MenuItem {
		return {
			messageKey: option.msgKey,
			on: () => {
				this.mainService.openPopup(
					[
						"process",
						path,
						this.route.snapshot.paramMap.get("type"),
						this.route.snapshot.paramMap.get("identity"),
						{
							id: option.id,
							level: option.level,
							targetSubtype: option.targetSubtype,
							targetType: option.targetType,
							associationType: option.associationType,
							sourceRole: option.sourceRole,
							msgKey: option.msgKey,
						},
					],
					this.route,
				);
			},
			iconClass: iconCSS,
			buttonCssClass: buttonCSS,
		};
	}

	getIconClasses(targetType: string, targetSubtype: string): string[] {
		const typeObject: stages.process.ViewableType = {
			ident: targetType,
			subtypeIdent: targetSubtype,
		};

		return this.viewService.getIconClasses(typeObject, false, false);
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.mainService.applicationState.activeFloatingActionButtonType.next(ActiveFloatingActionButtonType.NONE);
		this.destroy$.complete();
	}
}

function hasCreateAssociationPermission(element: ProcessView, sequenceOption: Sequence): boolean {
	let targetSubtype = "";
	let sourceRole = "";

	if (sequenceOption.targetSubtype) {
		targetSubtype = "." + sequenceOption.targetSubtype;
	}

	if (sequenceOption.sourceRole) {
		sourceRole = "$" + sequenceOption.sourceRole;
	}

	return element.associations
		? element.associations[
				sequenceOption.targetType + targetSubtype + ":" + sequenceOption.associationType + sourceRole
		  ].allowedOperations.CREATE_ASSOCIATION
		: false;
}
